import { Bell, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getNotification, getUnreadNotificationCount, markAllRead } from "@/sideEffects/api";
import { showApiError, showSuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { formatDateToLocal } from "@/lib/dateUtils";
import { LoadingOverlay } from "../LoadingOverlay";

export const NavNotifications = () => {
  const { t } = useTranslation();

  const { data: notificationsData, isLoading } = useQuery({
    queryFn: () => getNotification(),
    queryKey: ["notifications"],
    refetchOnMount: true,
  });

  const allNotifications = notificationsData?.data?.result || [];

  const { data: unreadCountData } = useQuery({
    queryFn: () => getUnreadNotificationCount(),
    queryKey: ["unreadCount"],
    refetchInterval: 10 * 1000,
  });

  const unreadCount = unreadCountData?.data || 0;

  const queryClient = useQueryClient();
  const markAllReadMutation = useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
    }
  };

  return (
    <DropdownMenu modal onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {/* Notification badge - show when there are unread notifications */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[350px]">
        {/* Header with Mark All Read button */}
        {allNotifications.length > 0 && (
          <div className="flex items-center justify-between p-2 border-b">
            <h4 className="text-sm font-medium">{t("notifications.title", "Notifications")}</h4>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isPending}
                className="h-8 text-xs"
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                {t("notifications.markAllRead")}
              </Button>
            )}
          </div>
        )}

        {/* Notifications content with scrolling */}
        <LoadingOverlay isLoading={isLoading}>
          <div className={cn("overflow-y-auto max-h-[400px]")}>
            {allNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-4 text-center">
                <Bell className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm font-medium text-muted-foreground">{t("notifications.noNotifications")}</p>
                <p className="text-xs text-muted-foreground">{t("notifications.noNotificationsDesc")}</p>
              </div>
            ) : (
              allNotifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    "flex flex-col items-start gap-1 p-3 border-b last:border-b-0",
                    !notification.isRead && "bg-muted/30"
                  )}
                >
                  <div className="flex items-start justify-between w-full">
                    <div className="font-medium">
                      {!notification.isRead && (
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-500 mr-1.5"></span>
                      )}
                      {notification.title}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">{notification.body}</div>
                  <div className="text-xs text-muted-foreground mt-2">{formatDateToLocal(notification.createdAt)}</div>
                </DropdownMenuItem>
              ))
            )}
          </div>
        </LoadingOverlay>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
